import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { getApiClient } from '../../utils/apiClient';
// Async thunks
export const fetchAllProjects = createAsyncThunk('project/fetchAllProjects', async (_, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/projects',
            method: 'get',
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const upsertProject = createAsyncThunk('project/upsertProject', async (projectName, { dispatch, rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/project',
            method: 'post',
            data: { projectName },
        });
        // Refresh projects list after successful upsert
        dispatch(fetchAllProjects());
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
export const removeProject = createAsyncThunk('project/removeProject', async (projectName, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/project/remove',
            method: 'post',
            data: { projectName },
        });
        return { projectName, ...response.data };
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Initial state
const initialState = {
    projects: [],
    loading: false,
    error: null,
};
// Project slice
const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        // Clear error state
        clearError: (state) => {
            state.error = null;
        },
        // Optimistically remove project (for immediate UI feedback)
        removeProjectOptimistic: (state, action) => {
            state.projects = state.projects.filter((p) => p !== action.payload.projectName);
        },
        // Add project locally (for immediate UI feedback)
        addProjectOptimistic: (state, action) => {
            if (!state.projects.includes(action.payload.projectName)) {
                state.projects.push(action.payload.projectName);
            }
        },
        // Set projects directly
        setProjects: (state, action) => {
            state.projects = action.payload.projects;
        },
    },
    extraReducers: (builder) => {
        // Fetch all projects
        builder
            .addCase(fetchAllProjects.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAllProjects.fulfilled, (state, action) => {
            state.loading = false;
            // Extract project names from the response (assuming response has objects with 'name' property)
            state.projects = action.payload.map((p) => p.name);
            state.error = null;
        })
            .addCase(fetchAllProjects.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Upsert project
        builder
            .addCase(upsertProject.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(upsertProject.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            // Note: projects list will be refreshed by the fetchAllProjects call
        })
            .addCase(upsertProject.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Remove project
        builder
            .addCase(removeProject.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            // Optimistically remove the project
            state.projects = state.projects.filter((p) => p !== action.meta.arg);
        })
            .addCase(removeProject.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            // Project already removed optimistically
        })
            .addCase(removeProject.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            // TODO: Consider reverting the optimistic update on error
        });
    },
});
// Export individual actions
export const { clearError, removeProjectOptimistic, addProjectOptimistic, setProjects, } = projectSlice.actions;
// Selectors
export const selectProjectState = (state) => state.project;
export const selectProjects = (state) => state.project.projects;
export const selectProjectsLoading = (state) => state.project.loading;
export const selectProjectsError = (state) => state.project.error;
export const selectProjectExists = (projectName) => (state) => state.project.projects.includes(projectName);
export const selectProjectsCount = (state) => state.project.projects.length;
export const selectProjectStartsWith = (state, inputText) => state.project.projects.filter((p) => p.toLowerCase().startsWith(inputText.toLowerCase()));
// Grouped exports for better organization
export const projectActions = {
    // Sync actions
    clearError,
    removeProjectOptimistic,
    addProjectOptimistic,
    setProjects,
    // Async actions
    fetchAllProjects,
    upsertProject,
    removeProject,
};
export const projectSelectors = {
    selectProjectState,
    selectProjects,
    selectProjectsLoading,
    selectProjectsError,
    selectProjectExists,
    selectProjectsCount,
    selectProjectStartsWith,
};
// Export reducer
export default projectSlice.reducer;
